/**
 * Finance → Control Presupuestario. Tabs por query param (?tab=), como Talent
 * Acquisition: P&L (real contra presupuesto desde BigQuery), Creators / Influencers
 * (spend de FP&A en creadores e influencers, Finance 07/10/2026), Company Metrics
 * (réplica del reporte CM 2.0, Finance 07/10/2026) y, para el admin de
 * la sección, Permisos (governance FINANCE_PNL_GOVERNANCE: Juan Cruz decide
 * quién ve el dashboard y quién pregunta en el Brain sobre Finance). La ruta la
 * gatea FinancePnlProtectedRoute y el router usa financePnlViewerProcedure:
 * misma audiencia en las dos puntas (política audit-roles).
 */

import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PnlDashboard } from '@/components/finance/budget-control/PnlDashboard';
import { CreatorsDashboard } from '@/components/finance/budget-control/CreatorsDashboard';
import { CompanyMetricsDashboard } from '@/components/finance/budget-control/CompanyMetricsDashboard';
import { FinancePnlGovernanceTab } from '@/components/finance/budget-control/FinancePnlGovernanceTab';
import { useFinancePnlAccess } from '@/hooks/useBudgetControl';

export function BudgetControlPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: access } = useFinancePnlAccess();
  const canAdmin = access?.canAdmin ?? false;
  const raw = searchParams.get('tab');
  const tab = raw === 'permisos' && canAdmin ? 'permisos' : raw === 'creators' ? 'creators' : raw === 'company' ? 'company' : 'pnl';
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30">
          <TrendingUp className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('budget_control.page.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('budget_control.page.subtitle')}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="pnl">{t('budget_control.page.tab_pnl')}</TabsTrigger>
          <TabsTrigger value="creators">{t('budget_control.page.tab_creators')}</TabsTrigger>
          <TabsTrigger value="company">{t('budget_control.page.tab_company')}</TabsTrigger>
          {canAdmin && <TabsTrigger value="permisos">{t('budget_control.page.tab_governance')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="pnl">
          <PnlDashboard />
        </TabsContent>
        <TabsContent value="creators">
          <CreatorsDashboard />
        </TabsContent>
        <TabsContent value="company">
          <CompanyMetricsDashboard />
        </TabsContent>
        {canAdmin && (
          <TabsContent value="permisos">
            <FinancePnlGovernanceTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
